import { getActions, withGlobal } from '../../global'
import type { FC } from '../../lib/teact/teact'
import React, { memo } from '../../lib/teact/teact'

import type { GlobalState } from '../../global/types'

import '../../global/actions/initial'
import useCurrentOrPrev from '../../hooks/useCurrentOrPrev'
import useHistoryBack from '../../hooks/useHistoryBack'
import { PLATFORM_ENV } from '../../util/windowEnvironment'

import Transition from '../ui/Transition'
import AuthCode from './AuthCode.async'
import AuthPassword from './AuthPassword.async'
import AuthPhoneNumber from './AuthPhoneNumber'
import AuthQrCode from './AuthQrCode'
import AuthRegister from './AuthRegister.async'

import { selectIsLoginQrFeatureEnabled } from '../../global/selectors/appConfig'
import './Auth.scss'

type StateProps = Pick<GlobalState, 'authState'> & {
  readonly isLoginQrFeatureEnabled: boolean
}

const Auth: FC<StateProps> = ({ authState, isLoginQrFeatureEnabled }) => {
  const { returnToAuthPhoneNumber, goToAuthQrCode } = getActions()

  const isMobile = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android'

  const handleChangeAuthorizationMethod = () => {
    if (isLoginQrFeatureEnabled && !isMobile) {
      goToAuthQrCode()
    } else {
      returnToAuthPhoneNumber()
    }
  }

  useHistoryBack({
    isActive:
      isLoginQrFeatureEnabled &&
      ((!isMobile && authState === 'authorizationStateWaitPhoneNumber') ||
        (isMobile && authState === 'authorizationStateWaitQrCode')),
    onBack: handleChangeAuthorizationMethod,
  })

  // For animation purposes
  const renderingAuthState = useCurrentOrPrev(authState !== 'authorizationStateReady' ? authState : undefined, true)

  function getScreen() {
    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return <AuthCode />
      case 'authorizationStateWaitPassword':
        return <AuthPassword />
      case 'authorizationStateWaitRegistration':
        return <AuthRegister />
      case 'authorizationStateWaitPhoneNumber':
        return <AuthPhoneNumber />
      case 'authorizationStateWaitQrCode':
        return <AuthQrCode />
      default:
        if (isLoginQrFeatureEnabled) {
          return isMobile ? <AuthPhoneNumber /> : <AuthQrCode />
        }

        return <AuthPhoneNumber />
    }
  }

  function getActiveKey() {
    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return 0
      case 'authorizationStateWaitPassword':
        return 1
      case 'authorizationStateWaitRegistration':
        return 2
      case 'authorizationStateWaitPhoneNumber':
        return 3
      case 'authorizationStateWaitQrCode':
        return 4
      default:
        if (isLoginQrFeatureEnabled) {
          return isMobile ? 3 : 4
        }

        return 3
    }
  }

  return (
    <Transition activeKey={getActiveKey()} name="fade" className="Auth">
      {getScreen()}
    </Transition>
  )
}

export default memo(
  withGlobal((global): StateProps => {
    const isLoginQrFeatureEnabled = selectIsLoginQrFeatureEnabled(global)

    return {
      authState: global.authState,
      isLoginQrFeatureEnabled,
    }
  })(Auth),
)
